<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'ContractTermEditor',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    contract: {
      type: Object,
      required: true,
    },
    term: {
      type: Object,
      required: false,
    },
    mode: {
      type: String,
      required: true,
    },
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const mainForm = ref(null);

  const $refs = {
    get mainForm() {
      return mainForm.value;
    },
  };

  const getConfiguration = (...args) => {
    switch (props.mode) {
      case 'add':
        return {
          isEditable: true,
          isDeletable: false,
          isTermReadonly: false,
        };

      case 'edit':
        return {
          isEditable: !props.term.locked,
          isDeletable: !props.term.locked,
          isTermReadonly: props.term.locked,
        };
    }
  };

  const formId = ref(null);
  const labelAlign = ref('right');
  const isEditable = ref(null);
  const isDeletable = ref(null);
  const isTermReadonly = ref(null);
  const messages = ref({
    add: {
      title: $fn.value.pgettext('Contract|ModalHeader', 'Add term'),
    },
    edit: {
      title: $fn.value.pgettext('Contract|ModalHeader', 'Edit term'),
    },
  });
  const item = ref(null);
  const currencies = ref(null);
  const services = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    noPrimaryButton: true,
    closeButtonText: $fn.value.gettext('Close'),
    size: 'md',
  }));
  const b_5731695935601903_dataSource = computed(() => ({
    name: 'suggest_product',
  }));

  const $data = computed(() => ({
    formId: formId.value,
    labelAlign: labelAlign.value,
    isEditable: isEditable.value,
    isDeletable: isDeletable.value,
    isTermReadonly: isTermReadonly.value,
    messages: messages.value,
    item: item.value,
    currencies: currencies.value,
    services: services.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_5731695935601903_dataSource: b_5731695935601903_dataSource.value,
  }));

  const $$load_formId = async () => {
    return $fn.value.randomElementId();
  };
  const $$load_isEditable = async () => {
    return getConfiguration().isEditable;
  };
  const $$load_isDeletable = async () => {
    return getConfiguration().isDeletable;
  };
  const $$load_isTermReadonly = async () => {
    return getConfiguration().isTermReadonly;
  };
  const $$load_item = async () => {
    return (
      props.term ||
      $fn.value.schemaDefaults('CreateContractTerm', {
        contract_id: props.contract.id,
      })
    );
  };
  const $$load_currencies = async () => {
    return $fn.value.fetch('list_currency');
  };
  const $$load_services = async () => {
    return $fn.value
      .fetch('suggest_product', {
        parameters: { query: '*', top: 50, skip: 0 },
      })
      .then((data) => _.orderBy(data, [(p) => p.name.toLowerCase()]));
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'formId' },
      { name: 'labelAlign' },
      { name: 'isEditable' },
      { name: 'isDeletable' },
      { name: 'isTermReadonly' },
      { name: 'messages' },
      { name: 'item' },
      { name: 'currencies' },
      { name: 'services' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_5731695935601903_dataSource', nodeId: '5731695935601903' },
    ],
    loaders: {
      formId: $$load_formId,
      isEditable: $$load_isEditable,
      isDeletable: $$load_isDeletable,
      isTermReadonly: $$load_isTermReadonly,
      item: $$load_item,
      currencies: $$load_currencies,
      services: $$load_services,
    },
    ux: {},
    dataRefs: {
      formId,
      labelAlign,
      isEditable,
      isDeletable,
      isTermReadonly,
      messages,
      item,
      currencies,
      services,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_5731695935601903_dataSource,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const currentObjectType = computed(() => {
    return 'contract_term';
  });
  const pageDocument = computed(() => {
    if (loaded.value) {
      return { title: messages.value[props.mode].title };
    }
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.contract,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('contract');
    }
  );
  watch(
    () => props.term,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('term');
    }
  );
  watch(
    () => props.mode,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('mode');
    }
  );
  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_a5ztzs8oo_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_closeModal_2ftiwb92k_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
        value: $event.data,
      },
    };
  };
  const act_1040167445267876_crud_y1wcxjik3_getActionArgs = async ($event) => {
    return {
      objectType: currentObjectType.value,
      op: 'upsert',
      data: item.value,
      options: {
        parameters: {
          rev: props.contract.revision_number,
        },
      },
    };
  };
  const act_5731695935601903_setData_qjow3tsd9_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'item.name',
      value: $event.data.option.name,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_5731695935601903_setData_6zs35wrwg_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'item.service_id',
      value: $event.data.option.id,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_5731695935601903_setData_jjoyplhpn_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'item.unit',
      value: $event.data.option.unit,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_2140198529698299_closeModal_xfw7tf2rm_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
      },
    };
  };
  const act_2140198529698299_request_mnkb25a49_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'delete_contract_term',
      data: item.value,
    };
  };

  const act_7315092382398562_closeModal_a5ztzs8oo =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_a5ztzs8oo_getActionArgs,
    });
  const inst_evh_7315092382398562_close_6kq0ql44w =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_a5ztzs8oo,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_6kq0ql44w],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_ena0ewemp =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_982xfyhih =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_ena0ewemp,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_982xfyhih],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_mv5m2rhof =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_n1k6asyw1 =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_mv5m2rhof,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_n1k6asyw1],
    event: 'close',
  });
  const act_1040167445267876_closeModal_2ftiwb92k =
    new actionTypes.CloseModalAction({
      actionArgs: act_1040167445267876_closeModal_2ftiwb92k_getActionArgs,
    });
  const inst_evh_1040167445267876_success_a0affa8rj =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeModal_2ftiwb92k,
      event: 'success',
      displayName: 'closeModal',
    });
  const act_1040167445267876_crud_y1wcxjik3 = new actionTypes.CRUDAction({
    actionArgs: act_1040167445267876_crud_y1wcxjik3_getActionArgs,
    displayName: 'save',
    events: [inst_evh_1040167445267876_success_a0affa8rj],
  });
  const inst_evh_1040167445267876_submit_b9azuxmtb =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_crud_y1wcxjik3,
      event: 'submit',
      displayName: 'save',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_b9azuxmtb],
    event: 'submit',
  });
  const act_5731695935601903_setData_qjow3tsd9 = new actionTypes.SetDataAction({
    actionArgs: act_5731695935601903_setData_qjow3tsd9_getActionArgs,
  });
  const inst_evh_5731695935601903_optionSelected_2tcplozhx =
    new actionTypes.EventHandlerImpl({
      action: act_5731695935601903_setData_qjow3tsd9,
      event: 'optionSelected',
      displayName: 'setData',
    });
  const act_5731695935601903_setData_6zs35wrwg = new actionTypes.SetDataAction({
    actionArgs: act_5731695935601903_setData_6zs35wrwg_getActionArgs,
  });
  const inst_evh_5731695935601903_optionSelected_630iubw2y =
    new actionTypes.EventHandlerImpl({
      action: act_5731695935601903_setData_6zs35wrwg,
      event: 'optionSelected',
      displayName: 'setData',
    });
  const act_5731695935601903_setData_jjoyplhpn = new actionTypes.SetDataAction({
    actionArgs: act_5731695935601903_setData_jjoyplhpn_getActionArgs,
  });
  const inst_evh_5731695935601903_optionSelected_7wrlofn7k =
    new actionTypes.EventHandlerImpl({
      action: act_5731695935601903_setData_jjoyplhpn,
      event: 'optionSelected',
      displayName: 'setData',
    });
  const inst_evh_5731695935601903_optionSelected =
    new actionTypes.EventHandlerGroup({
      handlers: [
        inst_evh_5731695935601903_optionSelected_2tcplozhx,
        inst_evh_5731695935601903_optionSelected_630iubw2y,
        inst_evh_5731695935601903_optionSelected_7wrlofn7k,
      ],
      event: 'optionSelected',
    });
  const act_2140198529698299_closeModal_xfw7tf2rm =
    new actionTypes.CloseModalAction({
      actionArgs: act_2140198529698299_closeModal_xfw7tf2rm_getActionArgs,
    });
  const inst_evh_2140198529698299_success_g89pba7ua =
    new actionTypes.EventHandlerImpl({
      action: act_2140198529698299_closeModal_xfw7tf2rm,
      event: 'success',
      displayName: 'closeModal',
    });
  const act_2140198529698299_request_mnkb25a49 = new actionTypes.RequestAction({
    actionArgs: act_2140198529698299_request_mnkb25a49_getActionArgs,
    displayName: 'deleteTerm',
    events: [inst_evh_2140198529698299_success_g89pba7ua],
  });
  const inst_evh_2140198529698299_clickPreventStop_jniungf1r =
    new actionTypes.EventHandlerImpl({
      action: act_2140198529698299_request_mnkb25a49,
      event: 'click.prevent.stop',
      displayName: 'deleteTerm',
    });
  const inst_evh_2140198529698299_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2140198529698299_clickPreventStop_jniungf1r],
      event: 'click.prevent.stop',
    });
  const act_2167826738365259_closeComponent_k9m00j0p5 =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2167826738365259_clickPreventStop_wf65dp9j6 =
    new actionTypes.EventHandlerImpl({
      action: act_2167826738365259_closeComponent_k9m00j0p5,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2167826738365259_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2167826738365259_clickPreventStop_wf65dp9j6],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_6kq0ql44w,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_982xfyhih,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_n1k6asyw1,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_success_a0affa8rj,
    inst_evh_1040167445267876_submit_b9azuxmtb,
    save: inst_evh_1040167445267876_submit_b9azuxmtb,
    inst_evh_1040167445267876_submit,
    inst_evh_5731695935601903_optionSelected_2tcplozhx,
    inst_evh_5731695935601903_optionSelected_630iubw2y,
    inst_evh_5731695935601903_optionSelected_7wrlofn7k,
    inst_evh_5731695935601903_optionSelected,
    inst_evh_2140198529698299_success_g89pba7ua,
    inst_evh_2140198529698299_clickPreventStop_jniungf1r,
    deleteTerm: inst_evh_2140198529698299_clickPreventStop_jniungf1r,
    inst_evh_2140198529698299_clickPreventStop,
    inst_evh_2167826738365259_clickPreventStop_wf65dp9j6,
    inst_evh_2167826738365259_clickPreventStop,
  };

  const evh_7315092382398562_close_6kq0ql44w = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_6kq0ql44w.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_982xfyhih = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_982xfyhih.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_n1k6asyw1 = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_n1k6asyw1.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_a0affa8rj = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_a0affa8rj.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_b9azuxmtb = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_b9azuxmtb.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5731695935601903_optionSelected_2tcplozhx = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_5731695935601903_optionSelected_2tcplozhx.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5731695935601903_optionSelected_630iubw2y = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_5731695935601903_optionSelected_630iubw2y.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5731695935601903_optionSelected_7wrlofn7k = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_5731695935601903_optionSelected_7wrlofn7k.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5731695935601903_optionSelected = async (event, scope) => {
    return await actions.inst_evh_5731695935601903_optionSelected.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_success_g89pba7ua = async (event, scope) => {
    return await actions.inst_evh_2140198529698299_success_g89pba7ua.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_clickPreventStop_jniungf1r = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2140198529698299_clickPreventStop_jniungf1r.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2140198529698299_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2167826738365259_clickPreventStop_wf65dp9j6 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2167826738365259_clickPreventStop_wf65dp9j6.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2167826738365259_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2167826738365259_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="ContractTermEditor"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :id="formId"
        name="contract"
        class="row"
        ref="mainForm"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <ItpCol>
          <ItpFormGroup
            name="service"
            :label="pgettext('ContractTerm', 'Service')"
            :label-cols-md="4"
            :content-cols-md="8"
            :labelAlign="labelAlign"
            v-if="!isTermReadonly"
          >
            <ItpSelect2
              name="client"
              v-model="item.service"
              :value="item.service"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (item.service =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
              bind-object
              :dataSource="b_5731695935601903_dataSource"
              textField="name"
              @optionSelected="evh_5731695935601903_optionSelected($event, {})"
            >
            </ItpSelect2>
          </ItpFormGroup>
          <ItpFormGroup
            name="name"
            :label="pgettext('ContractTerm', 'Name')"
            :label-cols-md="4"
            :content-cols-md="8"
            :labelAlign="labelAlign"
          >
            <ItpFormInput
              name="name"
              v-model="item.name"
              :value="item.name"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (item.name =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
              required
              :readonly="isTermReadonly"
            >
            </ItpFormInput>
          </ItpFormGroup>
          <ItpFormGroup
            name="valid_from"
            :label="pgettext('ContractTerm', 'Valid from')"
            :label-cols-md="4"
            :content-cols-md="4"
            :labelAlign="labelAlign"
          >
            <ItpFormDatetime
              name="valid_from"
              v-model="item.valid_from"
              :readonly="isTermReadonly"
            >
            </ItpFormDatetime>
          </ItpFormGroup>
          <ItpFormGroup
            name="valid_to"
            :label="pgettext('ContractTerm', 'Valid to')"
            :label-cols-md="4"
            :content-cols-md="4"
            :labelAlign="labelAlign"
          >
            <ItpFormDatetime
              name="valid_to"
              v-model="item.valid_to"
              :readonly="isTermReadonly"
            >
            </ItpFormDatetime>
          </ItpFormGroup>
          <hr />
          <ItpFormGroup
            :label="pgettext('ContractTerm', 'Amount')"
            :label-cols-md="4"
            :content-cols-md="8"
            :labelAlign="labelAlign"
            contentClass="input-group"
          >
            <ItpFormInput
              type="number"
              name="amount"
              v-model="item.amount"
              :value="item.amount"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (item.amount =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
              class="col-8"
            >
            </ItpFormInput>
            <ItpFormSelect2
              name="currency"
              v-model="item.currency"
              :value="item.currency"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (item.currency =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
              :options="currencies"
              textField="code"
              valueField="code"
              class="col-4"
            >
            </ItpFormSelect2>
          </ItpFormGroup>
          <ItpFormGroup
            name="quantity"
            :label="pgettext('ContractTerm', 'Quantity')"
            :label-cols-md="4"
            :content-cols-md="4"
            :labelAlign="labelAlign"
            contentClass="input-group"
          >
            <ItpFormInput
              type="number"
              name="quantity"
              v-model="item.quantity"
              :value="item.quantity"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (item.quantity =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
            >
            </ItpFormInput>
          </ItpFormGroup>
          <ItpFormGroup
            name="unit"
            :label="pgettext('ContractTerm', 'Unit')"
            :label-cols-md="4"
            :content-cols-md="4"
            :labelAlign="labelAlign"
            contentClass="input-group"
          >
            <ItpFormInput
              name="unit"
              v-model="item.unit"
              :value="item.unit"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (item.unit =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
            >
            </ItpFormInput>
          </ItpFormGroup>
          <ItpFormGroup
            name="notes"
            :label="pgettext('ContractTerm', 'Notes')"
            :label-cols-md="4"
            :content-cols-md="8"
            :labelAlign="labelAlign"
          >
            <ItpFormTextarea
              name="notes"
              v-model="item.notes"
              :value="item.notes"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (item.notes =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
            >
            </ItpFormTextarea>
          </ItpFormGroup>
        </ItpCol>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :text="pgettext('Button', 'Delete Term')"
          variant="danger"
          icon="fa-times"
          :spinning="actions.delete && actions.delete.isRunning"
          class="me-auto"
          v-if="mode === 'edit' && isDeletable"
          @click.prevent.stop="
            evh_2140198529698299_clickPreventStop($event, {})
          "
        >
        </ItpButton>
        <ItpButton
          :text="pgettext('Button', 'Save')"
          :disabled="!forms.contract || !forms.contract.submittable"
          type="submit"
          :form="formId"
          variant="primary"
          icon="fa-save"
          :spinning="actions.save && actions.save.isRunning"
          v-if="isEditable"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Close')"
          variant="default"
          @click.prevent.stop="
            evh_2167826738365259_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
